import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 110%; /* Scale everything by 1.1x */
    scroll-behavior: smooth !important;
    scroll-padding-top: 120px; /* Account for fixed header */
    overflow-x: hidden;
  }

  body {
    scroll-behavior: smooth !important;
  }

  /* Enhanced smooth scrolling for all scrollable elements */
  * {
    scroll-behavior: smooth !important;
  }

  /* Force smooth scrolling on all elements */
  html, body, div, section, article, aside, nav, header, footer, main {
    scroll-behavior: smooth !important;
  }

  /* Smooth transitions for all interactive elements */
  a, button, input, textarea, select, .btn, .card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Smooth page transitions */
  .page-transition {
    transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
  }

  .page-transition-enter {
    opacity: 0;
    transform: translateY(20px);
  }

  .page-transition-enter-active {
    opacity: 1;
    transform: translateY(0);
  }

  .page-transition-exit {
    opacity: 1;
    transform: translateY(0);
  }

  .page-transition-exit-active {
    opacity: 0;
    transform: translateY(-20px);
  }

  /* Smooth scrolling indicators */
  .scroll-indicator {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    transform-origin: left;
    z-index: 9999;
    transition: transform 0.1s ease-out;
  }

  /* Smooth reveal animations for sections */
  .reveal-on-scroll {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .reveal-on-scroll.revealed {
    opacity: 1;
    transform: translateY(0);
  }

  /* Custom scrollbar styling for webkit browsers */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 4px;
    transition: background 0.3s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.secondary};
  }

  /* Firefox scrollbar styling */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${props => props.theme.colors.primary} #f1f1f1;
  }

  body {
    font-family: ${props => props.theme.fonts.secondary};
    font-weight: 500;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.6;
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    overflow-x: hidden;
    padding-top: 120px; /* Account for fixed header */
    
    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      overflow-x: hidden;
      width: 100%;
      max-width: 100vw;
      padding-top: 110px;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      overflow-x: hidden;
      width: 100%;
      max-width: 100vw;
      padding-top: 100px;
    }
    
    @media (max-width: 768px) {
      overflow-x: hidden;
      width: 100%;
      max-width: 100vw;
      padding-top: 100px;
    }
  }

  /* Prevent scrolling when preloader is active */
  body.preloader-active {
    overflow: hidden;
    height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.heading};
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.heading};
  }

  h1 {
    font-size: 2.5rem;
    font-weight: 800;
    
    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 2.25rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 2rem;
    }
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  h2 {
    font-size: 2rem;
    
    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 1.875rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 1.75rem;
    }
    
    @media (max-width: 768px) {
      font-size: 1.75rem;
    }
  }

  h3 {
    font-size: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h4 {
    font-size: 1.25rem;
  }

  h5 {
    font-size: 1.1rem;
  }

  h6 {
    font-size: 1rem;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.7;
    font-weight: 500;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.3s ease;
  }

  input, textarea, select {
    font-family: inherit;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 4px;
    padding: 0.75rem;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight}40;
    }
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    
    @media (max-width: 768px) {
      padding: 0 1rem;
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 1rem;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    border: none;
    outline: none;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-primary {
    background-color: ${props => props.theme.colors.button};
    color: ${props => props.theme.colors.primary};
    font-weight: 700;

    &:hover:not(:disabled) {
      background-color: ${props => props.theme.colors.buttonDark};
      transform: translateY(-1px);
    }
  }

  .btn-secondary {
    background-color: transparent;
    color: ${props => props.theme.colors.primary};
    border: 2px solid ${props => props.theme.colors.primary};

    &:hover:not(:disabled) {
      background-color: ${props => props.theme.colors.secondary};
      color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.secondary};
    }
  }

  .btn-large {
    padding: 1rem 2rem;
    font-size: 1.1rem;
  }

  .btn-small {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }
  }

  .section {
    padding: 4rem 0;
    
    @media (max-width: 768px) {
      padding: 2rem 1rem;
    }
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .mb-1 { margin-bottom: 0.5rem; }
  .mb-2 { margin-bottom: 1rem; }
  .mb-3 { margin-bottom: 1.5rem; }
  .mb-4 { margin-bottom: 2rem; }
  .mb-5 { margin-bottom: 3rem; }

  .mt-1 { margin-top: 0.5rem; }
  .mt-2 { margin-top: 1rem; }
  .mt-3 { margin-top: 1.5rem; }
  .mt-4 { margin-top: 2rem; }
  .mt-5 { margin-top: 3rem; }

  .d-none { display: none; }
  .d-block { display: block; }
  .d-flex { display: flex; }
  .d-grid { display: grid; }

  .justify-center { justify-content: center; }
  .justify-between { justify-content: space-between; }
  .align-center { align-items: center; }

  .w-100 { width: 100%; }
  .h-100 { height: 100%; }

  /* Loading spinner */
  .loading {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Responsive utilities */
  .hide-mobile {
    @media (max-width: 768px) {
      display: none;
    }
  }

  .show-mobile {
    @media (min-width: 769px) {
      display: none;
    }
  }

  /* Accessibility */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Focus styles for keyboard navigation */
  *:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  /* Remove focus outline for navigation links */
  nav a:focus,
  header a:focus {
    outline: none;
    box-shadow: none;
  }

  /* CTA Button Style */
  .btn-cta {
    background-color: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.primary};
    font-weight: 700;

    &:hover:not(:disabled) {
      background-color: ${props => props.theme.colors.secondaryDark};
      transform: translateY(-1px);
    }
  }

  /* Enhanced main content scrolling */
  main {
    min-height: 100vh;
    scroll-behavior: smooth;
  }

  /* Smooth scrolling for all sections */
  section {
    scroll-margin-top: 120px;
  }

  /* Smooth scrolling for all headings */
  h1, h2, h3, h4, h5, h6 {
    scroll-margin-top: 120px;
  }
`;

export default GlobalStyles; 